/**
 * Location settings → Import tab.
 *
 * Lists the HangarMax layouts linked to this location and lets the user import
 * a new one or re-sync an existing one.
 */

import { useCallback, useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { Download, Link2, Loader2, RefreshCw, Trash2, Upload } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { useLocationContext } from '@/components/layout/LocationLayout'
import { useLocation as useLocationRecord } from '@/hooks/useLocations'
import {
  useDeleteHangarmaxImportSource,
  useExecuteHangarmaxImport,
  useHangarmaxImportSources,
  useImportProgress,
  useResyncHangarmaxImport,
} from '@/hooks/useHangarmaxImport'
import { HangarmaxImportDialog } from './HangarmaxImportDialog'
import { ImportResultReport } from './ImportResultReport'
import type { ExecuteResult } from '@/lib/hangarmax-import'

export function LocationImportTab() {
  const { locationId } = useLocationContext()
  const { data: location } = useLocationRecord(locationId)
  const { data: sources = [], isLoading } = useHangarmaxImportSources(locationId)

  const [dialogOpen, setDialogOpen] = useState(false)
  const [resyncResult, setResyncResult] = useState<ExecuteResult | null>(null)
  const [resyncingId, setResyncingId] = useState<string | null>(null)

  const resync = useResyncHangarmaxImport()
  const execute = useExecuteHangarmaxImport()
  const removeSource = useDeleteHangarmaxImportSource()
  const { setProgress } = useImportProgress()

  const locationCenter = useMemo(
    () =>
      location?.latitude != null && location?.longitude != null
        ? { lat: Number(location.latitude), lng: Number(location.longitude) }
        : null,
    [location?.latitude, location?.longitude]
  )

  const handleResync = useCallback(
    async (sourceId: string) => {
      if (!locationCenter) return
      setResyncingId(sourceId)
      setResyncResult(null)
      try {
        const prepared = await resync.mutateAsync({
          sourceId,
          locationId,
          locationCenter,
        })
        const result = await execute.mutateAsync({
          locationId,
          prepared,
          onProgress: setProgress,
        })
        setResyncResult(result)
      } finally {
        setResyncingId(null)
      }
    },
    [execute, locationCenter, locationId, resync, setProgress]
  )

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle>HangarMax layouts</CardTitle>
            <CardDescription>
              Import a layout designed in HangarMax — buildings, aircraft and
              procedural objects — and keep it in sync as the source changes.
              Imports never delete PlaneMAX data.
            </CardDescription>
          </div>
          <Button onClick={() => setDialogOpen(true)} disabled={!locationCenter}>
            <Upload className="mr-2 h-4 w-4" />
            Import layout
          </Button>
        </CardHeader>

        <CardContent className="space-y-3">
          {!locationCenter && (
            <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Set this location's latitude and longitude in General settings
              before importing — they define where the HangarMax scene lands.
            </p>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading imports…
            </div>
          )}

          {!isLoading && sources.length === 0 && (
            <p className="text-sm text-muted-foreground">
              No HangarMax layouts imported yet.
            </p>
          )}

          {sources.map((source) => (
            <div
              key={source.id}
              className="flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2"
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  {source.sourceKind === 'share_link' ? (
                    <Link2 className="h-3.5 w-3.5 text-muted-foreground" />
                  ) : (
                    <Download className="h-3.5 w-3.5 text-muted-foreground" />
                  )}
                  <span className="truncate text-sm font-medium">
                    {source.layoutName ?? 'Imported layout'}
                  </span>
                  <Badge variant="outline" className="text-[10px]">
                    {source.sourceKind === 'share_link' ? 'Share link' : 'Export file'}
                  </Badge>
                </div>
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {source.lastSyncedAt
                    ? `Synced ${formatDistanceToNow(new Date(source.lastSyncedAt), {
                        addSuffix: true,
                      })}`
                    : 'Never synced'}
                  {source.counts &&
                    ` · ${source.counts.created} created, ${source.counts.updated} updated, ${source.counts.unmatched} not imported`}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!locationCenter || resyncingId !== null}
                  onClick={() => void handleResync(source.id)}
                >
                  {resyncingId === source.id ? (
                    <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-3.5 w-3.5" />
                  )}
                  Re-sync
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeSource.mutate({ id: source.id, locationId })}
                  title="Remove the link (imported data stays)"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {resyncResult && (
        <Card>
          <CardHeader>
            <CardTitle>Last re-sync</CardTitle>
          </CardHeader>
          <CardContent>
            <ImportResultReport result={resyncResult} />
          </CardContent>
        </Card>
      )}

      <HangarmaxImportDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        locationId={locationId}
        locationCenter={locationCenter}
      />
    </div>
  )
}
